import { useTranslation } from "react-i18next";
import { formatNumberDigits, isArabicLanguage } from "src/utils/numberFormat";

export interface RestaurantFormatters {
  isRTL: boolean;
  // eslint-disable-next-line no-unused-vars
  formatNumber: (value: number, decimals?: number) => string;
  // eslint-disable-next-line no-unused-vars
  formatPercent: (value: number, decimals?: number) => string;
  // eslint-disable-next-line no-unused-vars
  formatCurrency: (value: number, options?: { compact?: boolean; decimals?: number }) => string;
  /** Currency with two decimals, for averages such as AOV. */
  // eslint-disable-next-line no-unused-vars
  formatCurrencyAverage: (value: number) => string;
  /** Localized "28.6 min" / "٢٨٫٦ دقيقة". */
  // eslint-disable-next-line no-unused-vars
  formatMinutes: (value: number, decimals?: number) => string;
  // eslint-disable-next-line no-unused-vars
  formatDate: (isoDate: string) => string;
}


const COMPACT_SUFFIX = { million: "M", thousand: "K" } as const;

function compactNumber(value: number, decimals: number, isRTL: boolean): string {
  const abs = Math.abs(value);
  let divided = value;
  let suffix = "";

  if (abs >= 1_000_000) {
    divided = value / 1_000_000;
    suffix = COMPACT_SUFFIX.million;
  } else if (abs >= 1_000) {
    divided = value / 1_000;
    suffix = COMPACT_SUFFIX.thousand;
  }

  const formatted = formatNumberDigits(
    divided,
    { minimumFractionDigits: 0, maximumFractionDigits: suffix ? decimals : 0 },
    isRTL ? "ar" : "en",
  );
  return `${formatted}${suffix}`;
}

export function useRestaurantFormatters(): RestaurantFormatters {
  const { i18n } = useTranslation();
  const isRTL = isArabicLanguage(i18n.language);

  const formatNumber = (value: number, decimals = 0) =>
    formatNumberDigits(
      value,
      { minimumFractionDigits: decimals, maximumFractionDigits: decimals },
      isRTL ? "ar" : "en",
    );

  const formatPercent = (value: number, decimals = 1) => {
    const formatted = formatNumberDigits(
      value * 100,
      { minimumFractionDigits: decimals, maximumFractionDigits: decimals },
      isRTL ? "ar" : "en",
    );
    return `${formatted}%`;
  };

  const formatCurrency = (value: number, options?: { compact?: boolean; decimals?: number }) => {
    const compact = options?.compact ?? false;
    const decimals = options?.decimals ?? (compact ? 1 : 0);
    const numeric = compact
      ? compactNumber(value, decimals, isRTL)
      : formatNumberDigits(value, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }, isRTL ? "ar" : "en");
    // The currency code stays Latin `SAR` in Arabic too — like the compact
    // `M`/`K` suffixes, it is read as a symbol rather than translated to `ر.س`.
    return `SAR ${numeric}`;
  };

  /**
   * Averages (AOV and friends) keep two decimals — rounding an average to whole
   * riyals hides the precision the figure is claiming to have.
   */
  const formatCurrencyAverage = (value: number) => formatCurrency(value, { decimals: 2 });

  const formatMinutes = (value: number, decimals = 1) => {
    const numeric = formatNumber(value, decimals);
    return isRTL ? `${numeric} دقيقة` : `${numeric} min`;
  };

  const formatDate = (isoDate: string) => {
    const parsed = new Date(`${isoDate}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) {
      return isoDate;
    }
    return new Intl.DateTimeFormat(isRTL ? "ar-EG-u-nu-arab" : "en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    }).format(parsed);
  };

  return {
    isRTL,
    formatNumber,
    formatPercent,
    formatCurrency,
    formatCurrencyAverage,
    formatMinutes,
    formatDate,
  };
}
